import dayjs from 'dayjs'
import debounce from 'debounce-fn'
import Debug from 'debug'
import {
  action,
  computed,
  makeAutoObservable,
  observable,
  onBecomeObserved,
  onBecomeUnobserved,
  reaction,
  set,
} from 'mobx'
import { mapValues, pick, pipe } from 'remeda'
import { isString } from 'remeda'
import Cookies from 'universal-cookie'

import { getBrandName } from '@jotta/config/brand'
import { createObservableRef } from '@jotta/hooks'
import type { Rect } from '@jotta/types/Dimensions'
import { fileRouteRegEx } from '@jotta/types/schemas/FileRouteSchema'
import { objectKeys } from '@jotta/types/TypeUtils'
import { isFolderInputSupported } from '@jotta/utils/browserFeatureChecks'
import { px } from '@jotta/utils/css'
import { detectScrollbarWidth } from '@jotta/utils/detectScrollbarWidth'
import { ensureValidNumber } from '@jotta/utils/typeAssertions'

import { breakpoints } from '../../themes/breakpoints'
import type { BrandTheme } from '../../types/BrandTheme'
import { getResponsiveValue } from '../../utils/getResponsiveValue'
import { getThemeCSSVariables } from '../../utils/getThemeCSSVariables'
import { getCSSCustomProperty } from '../../utils/setCSSCustomProperty'
const cookies = new Cookies()
function setWideDrawerCookie(isOpen: boolean) {
  document.cookie = `wideDrawerPreference=${isOpen}; Path=/; Domain=${window.location.hostname}; expires=Fri, 31 Dec 9999 23:59:59 GMT;`
}
export type AppLayoutSlot = keyof BrandStore['slots']

function getDocumentRect(): Rect {
  const rect = document.documentElement.getBoundingClientRect()
  // toJSON doesn't exist in Jest and breaks the tests
  if (rect.toJSON) {
    return rect.toJSON()
  }
  return rect
}
export class BrandStore {
  theme: BrandTheme
  activeElements = {
    timelineScrubber: false,
    topNav: fileRouteRegEx.test(window.location.pathname),
    moduleNav: true,
    moduleNavOverlay: true,
    // Should have an initial value of false on public pages
    leftNav: !/^\/(s|share|download)\/./.test(window.location.pathname),
  }
  slots = {
    timelineScrubber: createObservableRef<HTMLDivElement>(),
    moduleNav: createObservableRef<HTMLDivElement>(),
    moduleNavOverlay: createObservableRef<HTMLDivElement>(),
    leftNav: createObservableRef<HTMLDivElement>(),
    actionButton: createObservableRef<HTMLDivElement>(),
    uploadStatus: createObservableRef<HTMLDivElement>(),
    bottomStatusBar: createObservableRef<HTMLDivElement>(),
    header: createObservableRef<HTMLDivElement>(),
    overlay: createObservableRef<HTMLDivElement>(),
  }
  slotCounts: Record<AppLayoutSlot, number> = {
    timelineScrubber: 0,
    moduleNav: 0,
    moduleNavOverlay: 0,
    leftNav: 0,
    actionButton: 0,
    bottomStatusBar: 0,
    uploadStatus: 0,
    header: 0,
    overlay: 0,
  }
  registerSlot(slot: AppLayoutSlot) {
    this.slotCounts[slot]++
  }
  unregisterSlot(slot: AppLayoutSlot) {
    this.slotCounts[slot]--
  }
  getSlot(slot: AppLayoutSlot) {
    if (this.slotCounts[slot]) {
      return this.slots[slot]
    }
  }
  // Width of window, including scrollbar
  windowSize = {
    width: 0,
    height: 0,
  }
  // Size of viewport, excluding scrollbars
  rect: Rect
  scroll = {
    top: 0,
    scrollHeight: 0,
  }
  headerHeight: number
  scrollUpdatedAt = 0
  scrollbarWidth = detectScrollbarWidth()
  private breakpointObserver: ResizeObserver
  private resizeObserver: ResizeObserver
  private scrollElement: Element
  private _wideDrawerPreference: boolean =
    String(cookies.get('wideDrawerPreference')) === 'true'
  private openState: number = -1
  private log = Debug('jotta:branding:BrandStore')
  currentBreakpoint: number
  constructor(theme: BrandTheme) {
    this.log(
      'Create store %s',
      theme.branding.brand,
      cookies.get('wideDrawerPreference'),
    )
    this.headerHeight = theme.branding.header.height
    this.theme = theme
    /**
     * Setting correct initial dimensions allows scroll restoration to work
     * automatically with react-query when creating virtual lists based on these
     * dimensions. If they are briefly 0 when you click back the scroll resets to top.
     */
    this.rect = observable<Rect>(getDocumentRect())
    this.scroll.top = document.documentElement.scrollTop
    this.scroll.scrollHeight = document.documentElement.scrollHeight
    this.windowSize.width = window.innerWidth
    this.windowSize.height = window.innerHeight
    this.currentBreakpoint = this.getBreakPoint()
    makeAutoObservable<
      typeof this,
      'resizeObserver' | 'breakpointObserver' | 'log'
    >(
      this,
      {
        activateElements: action.bound,
        breakpoints: computed.struct,
        breakpointObserver: false,
        globalStyles: computed.struct,
        log: false,
        onResize: false,
        onResizeThrottled: false,
        onScrollThrottled: false,
        resizeObserver: false,
        theme: observable.struct,
      },
      {
        autoBind: true,
      },
    )
    this.breakpointObserver = new window.ResizeObserver(this.updateBreakpoint)
    this.breakpointObserver.observe(document.documentElement)
    this.resizeObserver = new window.ResizeObserver(this.onResizeThrottled)
    this.resizeObserver.observe(document.documentElement)
    this.scrollElement = document.documentElement
    onBecomeObserved(this.scroll, 'top', this.startObserveScrolling)
    onBecomeUnobserved(this.scroll, 'top', this.stopObserveScrolling)
    reaction(
      () => [this.currentBreakpoint],
      () => {
        isFolderInputSupported.clear()
      },
    )
  }
  activateElements(elements: Partial<BrandStore['activeElements']>) {
    this.log('activateElements', elements)
    set(this.activeElements, elements)
  }
  get hasScrubber() {
    return (
      !this.hasOverlay &&
      (this.activeElements.timelineScrubber ||
        Boolean(this.slotCounts.timelineScrubber))
    )
  }
  get hasModuleNav() {
    return this.activeElements.moduleNav
  }
  get isMobile() {
    return this.currentBreakpoint <= 2
  }
  get wideDrawerPreference() {
    return this._wideDrawerPreference
  }
  set wideDrawerPreference(pref: boolean) {
    setWideDrawerCookie(pref)
    this._wideDrawerPreference = pref
  }
  get isMobileTimeline() {
    return (
      this.windowWidth <
      this.theme.branding.photos.timeline.mobileTimelineBreakpoint
    )
  }
  get isOpen() {
    const isOpen = this.openState === this.currentBreakpointIndex
    const wideOpen =
      this.currentBreakpointIndex >= 2 && this.wideDrawerPreference
    return isOpen || wideOpen
  }
  get sidebarWidth() {
    if (!this.hasDrawer) {
      return 0
    }
    if (this.isOpen) {
      return this.sidebarOpenWidth
    }
    return this.sidebarClosedWidth
  }
  get sidebarClosedWidth() {
    const w = getResponsiveValue(
      this.currentBreakpointIndex,
      this.theme.branding.sidebar.closedWidth,
    )
    if (isString(w)) {
      return parseInt(w, 10)
    }
    return w
  }
  get sidebarOpenWidth() {
    const w = getResponsiveValue(
      this.currentBreakpointIndex,
      this.theme.branding.sidebar.openWidth,
    )
    if (isString(w)) {
      return parseInt(w, 10)
    }
    return w
  }
  get hasDrawer() {
    return this.activeElements.leftNav || Boolean(this.slotCounts.leftNav)
  }
  get visibleScrollBarWidth() {
    return this.windowSize.width - this.viewportWidth
  }
  get windowWidth() {
    return this.windowSize.width
  }
  get viewportWidth() {
    return this.rect.width
  }
  get viewportHeight() {
    return this.rect.height
  }
  get scrollTop() {
    return this.scroll.top
  }
  get scrollHeight() {
    return this.scroll.scrollHeight
  }
  get scrollPercent() {
    if (!this.scrollTop) {
      return 0
    }
    return (this.scrollTop / (this.scrollHeight - this.viewportHeight)) * 100
  }
  get breakpoints(): number[] {
    return [0, ...breakpoints]
  }
  private getBreakPoint() {
    // For browsers
    if (window && window.matchMedia) {
      return this.breakpoints.filter(
        bp => window.matchMedia(`screen and (min-width: ${bp}px)`).matches,
      ).length
    }
    // For jest jsdom that doesn't understand window.matchMedia
    const i = this.breakpoints.findIndex(br => br >= window.innerWidth)
    if (i >= 0) {
      return i + 1
    }
    return this.breakpoints.length
  }
  get breakpointRanges() {
    const maxIndex = this.breakpoints.length - 1
    return this.breakpoints.map((min, i) => {
      const max: number = i < maxIndex ? this.breakpoints[i + 1] - 1 : Infinity
      return {
        min,
        max,
        isCurrent: i === this.currentBreakpoint,
      }
    })
  }
  get currentBreakpointIndex() {
    return this.currentBreakpoint - 1
  }
  isTouchDevice() {
    if (window && window.matchMedia) {
      return !window.matchMedia('(hover: hover) and (pointer: fine)').matches
    }
    return false
  }
  get showHamburger() {
    return this.hasDrawer && this.theme.branding.header.showHamburger
  }
  get showLearnMoreWaitlistLink() {
    return this.theme.branding.learnMoreLinkWaitlist
  }
  get showLearnMoreSearchLink() {
    return this.theme.branding.learnMoreLinkSearch
  }
  get hasOverlay() {
    return Boolean(this.slotCounts.overlay)
  }
  get hasBottomToolbar() {
    return Boolean(this.slotCounts.bottomStatusBar)
  }
  get bottomToolbarHeight() {
    return this.hasBottomToolbar ? this.theme.branding.bottomToolBar.height : 0
  }
  /**
   * Width of content area, excluding measured scrollbar
   * This value should be stable even if scrollbars appear or disappear
   */
  get contentWidthSafe() {
    if (this.isMobile) {
      return this.windowWidth - this.scrubberWidth - this.scrollbarWidth
    }
    return (
      this.windowWidth -
      this.scrubberWidth -
      this.contentLeft -
      this.scrollbarWidth
    )
  }
  /**
   * Safe content width rounded to 100 pixels
   * This causes less recomputations if an approximate value is good enough
   */
  get contentWidthRounded() {
    const roundby = 100
    return Math.round(this.contentWidthSafe / roundby) * roundby
  }
  get contentLeft() {
    if (this.isMobile) {
      return 0
    }
    return this.sidebarWidth
  }
  get scrubberWidth() {
    if (!this.hasScrubber) {
      return 0
    }
    return getResponsiveValue(
      this.currentBreakpointIndex,
      this.theme.branding.photos.timeline.scrubberWidth,
    )
  }
  get brandName() {
    return getBrandName(this.theme.branding.brand)
  }
  get globalStyles() {
    return getThemeCSSVariables({
      theme: this.theme,
      scrollbarWidth: this.scrollbarWidth,
      headerHeight: this.headerHeight,
      activeElements: this.activeElements,
    })
  }
  get toJSON(): Record<string, string> {
    const debugKeys: (keyof BrandStore)[] = [
      'currentBreakpoint',
      'currentBreakpointIndex',
      'windowWidth',
      'windowWidth',
      'viewportWidth',
      'viewportHeight',
      'headerHeight',
      'contentWidthSafe',
      'contentLeft',
      'scrubberWidth',
      'scrollbarWidth',
      'visibleScrollBarWidth',
      'hasDrawer',
    ]
    return pipe(this, pick(debugKeys), mapValues(String))
  }
  get scrollInfo(): Record<string, string> {
    return {
      scrollTop: px(this.scrollTop),
      scrollPercent: `${Math.round(this.scrollPercent)}%`,
      scrollHeight: px(this.scrollHeight),
      scrollbarWidth: px(this.scrollbarWidth),
      visibleScrollBarWidth: px(this.visibleScrollBarWidth),
      scrollUpdatedAt: dayjs(this.scrollUpdatedAt).format('DD.MM.YY HH:ss'),
    }
  }
  startObserveScrolling() {
    this.updateScroll()
    this.scrollElement.addEventListener('scroll', this.onScrollThrottled, {
      passive: true,
    })
  }
  stopObserveScrolling() {
    this.onScrollThrottled.cancel()
    this.scrollElement.removeEventListener('scroll', this.onScrollThrottled)
  }
  setScrollElement(element: Element) {
    this.stopObserveScrolling()
    this.scrollElement = element
    this.startObserveScrolling()
  }
  closeDrawer() {
    this.log('closeDrawer', this.currentBreakpointIndex)
    // Persist state at the largest breakpoint
    if (this.currentBreakpointIndex >= 2) {
      setWideDrawerCookie(false)
      this.wideDrawerPreference = false
    }
    this.openState = -1
  }
  openDrawer() {
    this.log('openDrawer', this.currentBreakpointIndex)
    // Persist state at the largest breakpoint
    if (this.currentBreakpointIndex >= 2) {
      setWideDrawerCookie(true)
      this.wideDrawerPreference = true
    }
    this.openState = this.currentBreakpointIndex
  }
  toggleDrawer() {
    this.log('Toggle')
    if (this.isOpen) {
      this.closeDrawer()
    } else {
      this.openDrawer()
    }
  }

  onNavigate() {
    if (this.hasDrawer && this.currentBreakpoint < 3) {
      this.closeDrawer()
    }
  }
  updateBreakpoint = action('updateBreakpoint', () => {
    const br = this.getBreakPoint()
    if (br !== this.currentBreakpoint) {
      this.currentBreakpoint = br
    }
  })
  updateScroll = action('updateScroll', (e?: Event) => {
    const documentElement = this.scrollElement
    const top = documentElement.scrollTop
    const scrollHeight = documentElement.scrollHeight
    const windowWidth = window.innerWidth
    let updatedAt = this.scrollUpdatedAt
    if (top !== this.scroll.top) {
      this.scroll.top = top
      updatedAt = Date.now()
    }
    if (scrollHeight !== this.scroll.scrollHeight) {
      this.scroll.scrollHeight = scrollHeight
      updatedAt = Date.now()
    }
    if (windowWidth !== this.windowSize.width) {
      this.windowSize.width = windowWidth
      updatedAt = Date.now()
    }
    if (updatedAt !== this.scrollUpdatedAt) {
      this.scrollUpdatedAt = updatedAt
    }
  })
  updateSize = action('updateSize', (rect: Rect) => {
    for (const key of objectKeys(rect)) {
      if (this.rect[key] !== rect[key]) {
        if (key === 'width') {
          const removedBodyScrollBarSize = ensureValidNumber(
            getCSSCustomProperty(
              '--removed-body-scroll-bar-size',
              document.body,
            ),
            0,
          )
          this.rect[key] = rect[key] - removedBodyScrollBarSize
        } else {
          this.rect[key] = rect[key]
        }
      }
    }
    this.updateScroll()
  })
  onResize = action<ResizeObserverCallback>('onResize', ([entry]) => {
    if (entry) {
      const rect = entry.contentRect.toJSON() as Rect
      // requestAnimationFrame(() => this.updateSize(rect))
      this.updateSize(rect)
    }
  })
  onResizeThrottled = debounce(this.onResize, {
    wait: 100,
    maxWait: 600,
  })
  onScrollThrottled = debounce(this.updateScroll, {
    wait: 100,
    maxWait: 600,
  })
  private _hideFab = false
  public get hideFab() {
    return this._hideFab
  }
  public set hideFab(hidden: boolean) {
    this._hideFab = hidden
  }
}
let brandTheme: BrandTheme
let brandStore: BrandStore
export function getbrandStore(theme: BrandTheme) {
  if (!brandStore || brandTheme !== theme) {
    brandTheme = theme
    brandStore = new BrandStore(theme)
  }
  return brandStore
}
